<div class="login-modal">
  <div class="login-header">
    <h2 class="login-title">
      <mat-icon class="login-icon">account_circle</mat-icon>
      Welcome
    </h2>
    <p class="login-subtitle">Sign in or create an account</p>
  </div>

  <mat-tab-group class="auth-tabs" [selectedIndex]="currentTabIndex" (selectedTabChange)="onTabChange($event)">
    <mat-tab label="Sign In">
      <div class="tab-content">
        <form [formGroup]="loginForm" (ngSubmit)="signIn()" class="auth-form">
          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email Address</mat-label>
              <input
                matInput
                type="email"
                formControlName="email"
                placeholder="Enter your email"
                autocomplete="email"
              />
              <mat-icon matSuffix>email</mat-icon>
              @if (loginForm.get('email')?.invalid && loginForm.get('email')?.touched) {
                <mat-error>
                  @if (loginForm.get('email')?.errors?.['required']) {
                    <span>Email is required</span>
                  }
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Password</mat-label>
              <input
                matInput
                [type]="hidePassword ? 'password' : 'text'"
                formControlName="password"
                placeholder="Enter your password"
                autocomplete="current-password"
              />
              <button
                mat-icon-button
                matSuffix
                type="button"
                (click)="hidePassword = !hidePassword"
                [attr.aria-label]="'Toggle password visibility'"
                [attr.aria-pressed]="hidePassword"
              >
                <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
              @if (loginForm.get('password')?.invalid && loginForm.get('password')?.touched) {
                <mat-error>Password is required</mat-error>
              }
            </mat-form-field>
          </div>

          <div class="form-options">
            <mat-checkbox formControlName="rememberMe" class="remember-me">Remember me</mat-checkbox>
            <a href="#" class="forgot-password" (click)="forgotPassword($event)">Forgot password?</a>
          </div>

          @if (errorMessage) {
            <div class="error-message">
              <mat-icon>error</mat-icon>
              <span>{{ errorMessage }}</span>
            </div>
          }

          @if (successMessage) {
            <div class="success-message">
              <mat-icon>check_circle</mat-icon>
              <span>{{ successMessage }}</span>
            </div>
          }

          <div class="form-actions">
            <button
              mat-raised-button
              color="primary"
              type="submit"
              class="auth-button"
              [disabled]="loginForm.invalid || isLoading"
            >
              @if (isLoading) {
                <mat-spinner diameter="20"></mat-spinner>
                <span>Signing In...</span>
              } @else {
                <ng-container>
                  <mat-icon>login</mat-icon>
                  <span>Sign In</span>
                </ng-container>
              }
            </button>
          </div>
        </form>
      </div>
    </mat-tab>

    <mat-tab label="Sign Up">
      <div class="tab-content">
        <form [formGroup]="signupForm" (ngSubmit)="signUp()" class="auth-form">
          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email Address</mat-label>
              <input
                matInput
                type="email"
                formControlName="email"
                placeholder="Enter your email"
                autocomplete="email"
              />
              <mat-icon matSuffix>email</mat-icon>
              @if (signupForm.get('email')?.invalid && signupForm.get('email')?.touched) {
                <mat-error>
                  @if (signupForm.get('email')?.errors?.['required']) {
                    <span>Email is required</span>
                  }
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Password</mat-label>
              <input
                matInput
                [type]="hideSignupPassword ? 'password' : 'text'"
                formControlName="password"
                placeholder="Create a password"
                autocomplete="new-password"
              />
              <button
                mat-icon-button
                matSuffix
                type="button"
                (click)="hideSignupPassword = !hideSignupPassword"
                [attr.aria-label]="'Toggle password visibility'"
                [attr.aria-pressed]="hideSignupPassword"
              >
                <mat-icon>{{ hideSignupPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
              <mat-hint>Password must be at least 8 characters long</mat-hint>
              @if (signupForm.get('password')?.invalid && signupForm.get('password')?.touched) {
                <mat-error>
                  @if (signupForm.get('password')?.errors?.['required']) {
                    <span>Password is required</span>
                  }
                  @if (signupForm.get('password')?.errors?.['minlength']) {
                    <span>Password must be at least 8 characters</span>
                  }
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Confirm Password</mat-label>
              <input
                matInput
                [type]="hideConfirmPassword ? 'password' : 'text'"
                formControlName="confirmPassword"
                placeholder="Confirm your password"
                autocomplete="new-password"
              />
              <button
                mat-icon-button
                matSuffix
                type="button"
                (click)="hideConfirmPassword = !hideConfirmPassword"
                [attr.aria-label]="'Toggle password visibility'"
                [attr.aria-pressed]="hideConfirmPassword"
              >
                <mat-icon>{{ hideConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
              @if (signupForm.get('confirmPassword')?.invalid && signupForm.get('confirmPassword')?.touched) {
                <mat-error>
                  @if (signupForm.get('confirmPassword')?.errors?.['required']) {
                    <span>Please confirm your password</span>
                  }
                  @if (signupForm.get('confirmPassword')?.errors?.['mismatch']) {
                    <span>Passwords do not match</span>
                  }
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-checkbox formControlName="agreeToTerms" class="terms-checkbox">
              I agree to the Terms of Service and Privacy Policy
            </mat-checkbox>
            @if (signupForm.get('agreeToTerms')?.invalid && signupForm.get('agreeToTerms')?.touched) {
              <mat-error>You must agree to the terms and conditions</mat-error>
            }
          </div>

          @if (errorMessage) {
            <div class="error-message">
              <mat-icon>error</mat-icon>
              <span>{{ errorMessage }}</span>
            </div>
          }

          @if (successMessage) {
            <div class="success-message">
              <mat-icon>check_circle</mat-icon>
              <span>{{ successMessage }}</span>
            </div>
          }

          <div class="form-actions">
            <button
              mat-raised-button
              color="primary"
              type="submit"
              class="auth-button"
              [disabled]="signupForm.invalid || isLoading"
            >
              @if (isLoading) {
                <mat-spinner diameter="20"></mat-spinner>
                <span>Creating Account...</span>
              } @else {
                <ng-container>
                  <mat-icon>person_add</mat-icon>
                  <span>Create Account</span>
                </ng-container>
              }
            </button>
          </div>
        </form>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>

@if (showVerificationModal) {
  <div class="verification-modal">
    <div class="verification-content">
      <div class="verification-header">
        <h3 class="verification-title">
          <mat-icon class="verification-icon">email</mat-icon>
          @if (loginForm.get('password')?.value) {
            Complete Your Sign In
          } @else {
            Verify Your Email
          }
        </h3>
        <p class="verification-subtitle">
          @if (loginForm.get('password')?.value) {
            Enter the verification code sent to <strong>{{ pendingEmail }}</strong> to finish signing in.
          } @else {
            We've sent a verification code to <strong>{{ pendingEmail }}</strong>
          }
        </p>
      </div>

      <form [formGroup]="verificationForm" (ngSubmit)="confirmSignUp()" class="verification-form">
        <div class="form-group">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Verification Code</mat-label>
            <input
              matInput
              type="text"
              formControlName="code"
              placeholder="Enter 6-digit code"
              maxlength="6"
              autocomplete="one-time-code"
            />
            <mat-icon matSuffix>security</mat-icon>
            <mat-hint>Check your email for the verification code</mat-hint>
            @if (verificationForm.get('code')?.invalid && verificationForm.get('code')?.touched) {
              <mat-error>
                @if (verificationForm.get('code')?.errors?.['required']) {
                  <span>Verification code is required</span>
                }
                @if (verificationForm.get('code')?.errors?.['minlength']) {
                  <span>Code must be 6 digits</span>
                }
                @if (verificationForm.get('code')?.errors?.['pattern']) {
                  <span>Code must contain only numbers</span>
                }
              </mat-error>
            }
          </mat-form-field>
        </div>

        @if (errorMessage) {
          <div class="error-message">
            <mat-icon>error</mat-icon>
            <span>{{ errorMessage }}</span>
          </div>
        }

        @if (successMessage) {
          <div class="success-message">
            <mat-icon>check_circle</mat-icon>
            <span>{{ successMessage }}</span>
          </div>
        }

        <div class="form-actions">
          <button
            mat-raised-button
            color="primary"
            type="submit"
            class="auth-button"
            [disabled]="verificationForm.invalid || isLoading"
          >
            @if (isLoading) {
              <mat-spinner diameter="20"></mat-spinner>
              <span>Verifying...</span>
            } @else {
              <ng-container>
                <mat-icon>verified</mat-icon>
                <span>Verify Email</span>
              </ng-container>
            }
          </button>
        </div>
      </form>

      <div class="verification-actions">
        <button mat-button color="primary" (click)="resendVerificationCode()" [disabled]="isResending">
          @if (isResending) {
            <mat-spinner diameter="16"></mat-spinner>
            <span>Resending...</span>
          } @else {
            <ng-container>
              <mat-icon>refresh</mat-icon>
              <span>Resend Code</span>
            </ng-container>
          }
        </button>

        @if (loginForm.get('password')?.value) {
          <button mat-button (click)="goBackToLogin()" [disabled]="isLoading">
            <mat-icon>arrow_back</mat-icon>
            <span>Back to Sign In</span>
          </button>
        } @else {
          <button mat-button (click)="goBackToSignup()" [disabled]="isLoading">
            <mat-icon>arrow_back</mat-icon>
            <span>Back to Sign Up</span>
          </button>
        }
      </div>
    </div>
  </div>
}

@if (showNewPasswordModal) {
  <div class="verification-modal">
    <div class="verification-content">
      <div class="verification-header">
        <h3 class="verification-title">
          <mat-icon class="verification-icon">lock_reset</mat-icon>
          Set a New Password
        </h3>
        <p class="verification-subtitle">
          Your password was set by an administrator and must be changed before you can continue.
        </p>
      </div>

      <form [formGroup]="newPasswordForm" (ngSubmit)="submitNewPassword()" class="verification-form">
        <div class="form-group">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>New Password</mat-label>
            <input
              matInput
              [type]="hideNewPassword ? 'password' : 'text'"
              formControlName="newPassword"
              placeholder="Enter a new password"
              autocomplete="new-password"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              (click)="hideNewPassword = !hideNewPassword"
              [attr.aria-label]="'Toggle password visibility'"
              [attr.aria-pressed]="hideNewPassword"
            >
              <mat-icon>{{ hideNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-hint>Password must be at least 8 characters long</mat-hint>
            @if (newPasswordForm.get('newPassword')?.invalid && newPasswordForm.get('newPassword')?.touched) {
              <mat-error>
                @if (newPasswordForm.get('newPassword')?.errors?.['required']) {
                  <span>New password is required</span>
                }
                @if (newPasswordForm.get('newPassword')?.errors?.['minlength']) {
                  <span>Password must be at least 8 characters</span>
                }
              </mat-error>
            }
          </mat-form-field>
        </div>

        <div class="form-group">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Confirm New Password</mat-label>
            <input
              matInput
              [type]="hideConfirmNewPassword ? 'password' : 'text'"
              formControlName="confirmNewPassword"
              placeholder="Confirm your new password"
              autocomplete="new-password"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              (click)="hideConfirmNewPassword = !hideConfirmNewPassword"
              [attr.aria-label]="'Toggle password visibility'"
              [attr.aria-pressed]="hideConfirmNewPassword"
            >
              <mat-icon>{{ hideConfirmNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            @if (newPasswordForm.get('confirmNewPassword')?.invalid && newPasswordForm.get('confirmNewPassword')?.touched) {
              <mat-error>
                @if (newPasswordForm.get('confirmNewPassword')?.errors?.['required']) {
                  <span>Please confirm your new password</span>
                }
                @if (newPasswordForm.get('confirmNewPassword')?.errors?.['mismatch']) {
                  <span>Passwords do not match</span>
                }
              </mat-error>
            }
          </mat-form-field>
        </div>

        @if (errorMessage) {
          <div class="error-message">
            <mat-icon>error</mat-icon>
            <span>{{ errorMessage }}</span>
          </div>
        }

        @if (successMessage) {
          <div class="success-message">
            <mat-icon>check_circle</mat-icon>
            <span>{{ successMessage }}</span>
          </div>
        }

        <div class="form-actions">
          <button
            mat-raised-button
            color="primary"
            type="submit"
            class="auth-button"
            [disabled]="newPasswordForm.invalid || isLoading"
          >
            @if (isLoading) {
              <mat-spinner diameter="20"></mat-spinner>
              <span>Updating Password...</span>
            } @else {
              <ng-container>
                <mat-icon>lock_reset</mat-icon>
                <span>Set New Password</span>
              </ng-container>
            }
          </button>
        </div>
      </form>

      <div class="verification-actions">
        <button mat-button (click)="goBackFromNewPassword()" [disabled]="isLoading">
          <mat-icon>arrow_back</mat-icon>
          <span>Back to Sign In</span>
        </button>
      </div>
    </div>
  </div>
}
